import { Space, Table, TableColumnsType, Typography } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import { ThirdPartyJobResponse } from 'store/manifests/types';
import { TableProps } from 'antd/es/table';
import TooltipWithCopy from 'components/ui/composed/tooltipWithCopy/TooltipWithCopy';

type Props = TableProps<ThirdPartyJobResponse> & {
    onEdit?: (job: ThirdPartyJobResponse) => void;
    onDelete?: (job: ThirdPartyJobResponse) => void;
};

const ThirdPartyJobsTable = ({ onEdit, onDelete, ...rest }: Props) => {
    const columns: TableColumnsType<ThirdPartyJobResponse> = [
        {
            title: 'Job ID',
            key: 'jobId',
            sorter: (a, b) => a.jobReferenceId.localeCompare(b.jobReferenceId),
            render: (_, record) => {
                return <TooltipWithCopy copyText={record.jobReferenceId}>{record.jobReferenceId}</TooltipWithCopy>;
            },
        },
        {
            title: 'Customer',
            key: 'customer',
            sorter: (a, b) => a.customer.name.localeCompare(b.customer.name),
            render: (_, record) => {
                return <Typography.Text>{record.customer.name}</Typography.Text>;
            },
        },
        {
            title: 'MRN',
            key: 'mrns',
            render: (_, record) => {
                return (
                    <Typography.Text>
                        {record.mrns.map((mrn) => (
                            <li key={mrn}>
                                <TooltipWithCopy copyText={mrn}>{mrn}</TooltipWithCopy>
                            </li>
                        ))}
                    </Typography.Text>
                );
            },
        },
        {
            title: 'Customs Broker',
            key: 'customsBroker',
            sorter: (a, b) => (a.customsBrokerName ?? '').localeCompare(b.customsBrokerName ?? ''),
            render: (_, record) => {
                return (
                    <div>
                        <div>
                            <Typography.Text>{record.customsBrokerName ?? '-'}</Typography.Text>
                        </div>
                        <div>
                            <Typography.Text>{record.customsBrokerEmail ?? '-'}</Typography.Text>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Broker Phone',
            key: 'brokerPhone',
            render: (_, record) => {
                const phoneCode = record?.customsBrokerPhoneCountryCode ?? '';
                const phoneNumber = record?.customsBrokerPhoneNumber ?? '';

                let formatted = '-';

                if (!phoneCode || !phoneNumber) {
                    return '-';
                }

                formatted = `${phoneCode} ${phoneNumber}`;
                return <Typography.Text>{formatted}</Typography.Text>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 1,
            render: (_, record) => {
                return (
                    <Space size={8}>
                        <IconButton icon={<EditOutlined />} tooltipText="Edit" onClick={() => onEdit?.(record)} />
                        <IconButton icon={<DeleteOutlined />} tooltipText="Remove" onClick={() => onDelete?.(record)} />
                    </Space>
                );
            },
        },
    ];

    return (
        <StyledTableWrapper>
            <Table<ThirdPartyJobResponse>
                rowKey="jobId"
                rowClassName="row-align-top"
                columns={columns}
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
                {...rest}
            />
        </StyledTableWrapper>
    );
};

export default ThirdPartyJobsTable;
